import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { normalizePhoneForWa } from "@/lib/messageEngine";
import SmartMessageComposer from "@/components/SmartMessageComposer";

/**
 * Compatibility wrapper — kept under the old `WhatsAppButton` name so existing
 * imports keep working. Now opens the unified `SmartMessageComposer` (multi-
 * channel: WhatsApp / Email / Copy) instead of a single-channel WhatsApp dialog.
 *
 * Props (legacy):
 *   phone, contactName, dealTitle, action, meetingWhen, meetingLocation,
 *   defaultTemplate, size, variant, label, testId, followupId, onSent
 */
export default function WhatsAppButton({
  phone,
  email,
  contactName,
  dealTitle,
  dealId,
  clientId,
  clientCompany,
  meetingWhen,
  meetingLocation,
  action,
  event,
  defaultTemplate,
  size = "sm",
  variant = "outline",
  label,
  testId,
  followupId,
  onSent,
}) {
  const [open, setOpen] = useState(false);

  // Render only when there's at least one usable channel (phone or email)
  const hasPhone = phone && normalizePhoneForWa(phone);
  if (!hasPhone && !email) return null;

  const isIconOnly = size === "icon" || size === "icon-sm";
  const isCompactIcon = size === "icon-sm";
  const buttonSize = isCompactIcon ? "icon" : size;
  const initialSituation = defaultTemplate || (action ? "follow_up_action" : meetingWhen ? "meeting_confirm" : dealTitle ? "follow_up_deal" : "intro");

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={buttonSize}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`rounded-2xl tap text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800 ${isCompactIcon ? "h-9 w-9" : ""}`}
        data-testid={testId || "whatsapp-button"}
      >
        <MessageCircle className={isIconOnly ? "h-4 w-4" : "h-3.5 w-3.5 mr-1"} />
        {!isIconOnly && (label || "Message")}
      </Button>

      <SmartMessageComposer
        open={open}
        onOpenChange={setOpen}
        contact={{ name: contactName, phone, email, company: clientCompany }}
        deal={dealTitle ? { id: dealId, title: dealTitle } : null}
        client={{ id: clientId, company_name: clientCompany, contact_person: contactName }}
        defaultSituation={initialSituation}
        action={action}
        meetingWhen={meetingWhen}
        meetingLocation={meetingLocation}
        event={event}
        followupId={followupId}
        onActivityLogged={onSent}
      />
    </>
  );
}
